import { useState } from "react";
import { ModalMode } from "apps/events/types/ModalMode";
import { EnumOwnerType, EventByIdQuery } from "gql/generated";
import { LocationListData } from "apps/events/types/LocationListData";
import { EventCreateEditContextProvider } from "../EventCreateEditContext/EventCreateEditContextProvider";
import { EventCreateEditModalContent } from "./EventCreateEditModalContent";

type EventCreateEditModalProps = {
  isOpen: boolean;
  userEmailAddress: string;
  userFirstName: string;
  userLastName: string;
  userType: EnumOwnerType | "PUBLIC";
  initialMode: ModalMode;
  initialStep?: number;
  // All users will have this. For OPERATOR users, this will be the first organization in their access list
  defaultOrganizationId: string;
  // Only OPERATOR users will have this. This will be the list of ALL operators they have access to
  defaultOperatorEntityIds: string[];
  /** Storefront connected listings offered as the event organizer. */
  organizerEntityIds?: string[];
  // Only OPERATOR users will have this. This will be the list of ALL organizations they have access to
  defaultOrganizationIdsForOperatorUser: string[];
  /** Organization tag ids pre-applied to new events (e.g. from the submit widget's `tag-ids`). */
  defaultOrganizationTagIds?: string[];
  // Only OPERATOR users will have this. This will be the operator entity ID to limit the event host to
  limitHostToOperatorEntityId?: string;
  /** Pre-built organizer location from the parent page, bypasses operatorMany query. */
  preloadedOrganizerLocation?: LocationListData;
  eventId?: string;
  onClose: () => void;
  onEventUpdated: (event: EventByIdQuery["eventById"]) => void;
  onEventDeleted: (eventId: string) => void;
  hideMenuButtons?: boolean;
  /** When true, the overflow menu shows Delete (organization admins or TNB internal). */
  isOrganizationAdmin?: boolean;
  /** When true, the overflow menu shows Delete (TNB internal users). */
  isTnbInternalUser?: boolean;
};

export const EventCreateEditModal = (props: EventCreateEditModalProps) => {
  const {
    isOpen,
    userEmailAddress,
    userFirstName,
    userLastName,
    userType,
    initialMode,
    initialStep,
    defaultOrganizationId,
    defaultOperatorEntityIds,
    organizerEntityIds,
    defaultOrganizationIdsForOperatorUser,
    defaultOrganizationTagIds,
    limitHostToOperatorEntityId,
    preloadedOrganizerLocation,
    eventId,
    onClose,
    onEventUpdated,
    onEventDeleted,
    hideMenuButtons,
    isOrganizationAdmin = false,
    isTnbInternalUser = false,
  } = props;

  const [personDetailsPersonId, setPersonDetailsPersonId] = useState<
    string | null
  >(null);
  const [isPersonDetailsOpen, setIsPersonDetailsOpen] = useState(false);
  const onOpenPersonDetails = (personId: string) => {
    setPersonDetailsPersonId(personId);
    setIsPersonDetailsOpen(true);
  };
  const onClosePersonDetails = () => {
    setIsPersonDetailsOpen(false);
    setTimeout(() => setPersonDetailsPersonId(null), 300);
  };

  return (
    <EventCreateEditContextProvider
      isOpen={isOpen}
      initialMode={initialMode}
      initialStep={initialStep}
      userEmailAddress={userEmailAddress}
      userFirstName={userFirstName}
      userLastName={userLastName}
      userType={userType}
      defaultOrganizationId={defaultOrganizationId}
      defaultOperatorEntityIds={defaultOperatorEntityIds}
      organizerEntityIds={organizerEntityIds}
      defaultOrganizationIdsForOperatorUser={
        defaultOrganizationIdsForOperatorUser
      }
      defaultOrganizationTagIds={defaultOrganizationTagIds}
      limitHostToOperatorEntityId={limitHostToOperatorEntityId}
      preloadedOrganizerLocation={preloadedOrganizerLocation}
      eventId={eventId}
      onClose={onClose}
      onEventUpdated={onEventUpdated}
      onEventDeleted={onEventDeleted}
      hideMenuButtons={hideMenuButtons}
      isOrganizationAdmin={isOrganizationAdmin}
      isTnbInternalUser={isTnbInternalUser}
      onOpenPersonDetails={onOpenPersonDetails}
      personDetailsPersonId={personDetailsPersonId}
      isPersonDetailsOpen={isPersonDetailsOpen}
      onClosePersonDetails={onClosePersonDetails}
      setPersonDetailsPersonId={setPersonDetailsPersonId}
    >
      <EventCreateEditModalContent />
    </EventCreateEditContextProvider>
  );
};
